import { PASSCODE_CACHE_NAME } from '../config';
import * as cacheApi from './cacheApi';

const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const PBKDF2_ITERATIONS = 600000;
const BLOB_VERSION = 1;
const BLOB_MAGIC = new TextEncoder().encode('SWPC2');
const LEGACY_SALT = ['harder better', 'faster stronger'].join(' ');

let currentPasscodeHash: ArrayBuffer | undefined;

export function getPasscodeHash() {
  return currentPasscodeHash;
}

export function setPasscodeHash(passcodeHash: ArrayBuffer) {
  currentPasscodeHash = passcodeHash;
}

export async function setupPasscode(passcode: string) {
  currentPasscodeHash = await getPasscodeSecret(passcode);
}

export async function encryptSession(sessionJson?: string, globalJson?: string) {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  await Promise.all([
    (async () => {
      if (!sessionJson) return;

      const sessionEncrypted = await aesEncrypt(sessionJson, currentPasscodeHash);
      await store('sessionEncrypted', sessionEncrypted);
    })(),
    (async () => {
      if (!globalJson) return;

      const globalEncrypted = await aesEncrypt(globalJson, currentPasscodeHash);
      await store('globalEncrypted', globalEncrypted);
    })(),
  ]);
}

export async function decryptSessionByCurrentHash() {
  if (!currentPasscodeHash) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing current passcode');
    throw new Error('[api/passcode] Missing current passcode');
  }

  const [sessionEncrypted, globalEncrypted] = await Promise.all([
    load('sessionEncrypted'),
    load('globalEncrypted'),
  ]);

  if (!sessionEncrypted || !globalEncrypted) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing required stored fields');
    throw new Error('[api/passcode] Missing required stored fields');
  }

  try {
    const [session, global] = await Promise.all([
      aesDecrypt(sessionEncrypted, currentPasscodeHash),
      aesDecrypt(globalEncrypted, currentPasscodeHash),
    ]);

    return { sessionJson: session.plaintext, globalJson: global.plaintext };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

export async function decryptSession(passcode: string) {
  const passcodeHash = await getPasscodeSecret(passcode);
  const legacyPasscodeHash = await getLegacyPasscodeHash(passcode);

  const [sessionEncrypted, globalEncrypted] = await Promise.all([
    load('sessionEncrypted'),
    load('globalEncrypted'),
  ]);

  if (!sessionEncrypted || !globalEncrypted) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Missing required stored fields');
    throw new Error('[api/passcode] Missing required stored fields');
  }

  try {
    const [session, global] = await Promise.all([
      aesDecrypt(sessionEncrypted, passcodeHash, legacyPasscodeHash),
      aesDecrypt(globalEncrypted, passcodeHash, legacyPasscodeHash),
    ]);

    currentPasscodeHash = passcodeHash;

    if (session.isLegacy || global.isLegacy) {
      await encryptSession(session.plaintext, global.plaintext);
    }

    return { sessionJson: session.plaintext, globalJson: global.plaintext };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api/passcode] Error decrypting session', err);
    throw err;
  }
}

export function forgetPasscode() {
  currentPasscodeHash = undefined;
}

export function clearEncryptedSession() {
  forgetPasscode();
  return cacheApi.clear(PASSCODE_CACHE_NAME);
}

function getPasscodeSecret(passcode: string) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(passcode));
}

function getLegacyPasscodeHash(passcode: string) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${passcode}${LEGACY_SALT}`));
}

async function store(key: string, value: ArrayBuffer) {
  const isSuccessful = await cacheApi.save(PASSCODE_CACHE_NAME, key, value);
  if (isSuccessful) {
    return;
  }

  throw new Error('Failed to save to cache');
}

function load(key: string) {
  return cacheApi.fetch(PASSCODE_CACHE_NAME, key, cacheApi.Type.ArrayBuffer);
}

async function aesEncrypt(plaintext: string, passcodeSecret: ArrayBuffer) {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const alg = { name: 'AES-GCM', iv };
  const key = await deriveAesKey(passcodeSecret, salt, ['encrypt']);
  const ptUint8 = new TextEncoder().encode(plaintext);
  const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8);
  const ct = new Uint8Array(ctBuffer);
  const headerLength = BLOB_MAGIC.length + 1 + 4 + 1;
  const result = new Uint8Array(headerLength + SALT_LENGTH + IV_LENGTH + ct.length);
  result.set(BLOB_MAGIC, 0);
  result[BLOB_MAGIC.length] = BLOB_VERSION;
  new DataView(result.buffer).setUint32(BLOB_MAGIC.length + 1, PBKDF2_ITERATIONS, false);
  result[BLOB_MAGIC.length + 5] = SALT_LENGTH;
  result.set(salt, headerLength);
  result.set(iv, headerLength + SALT_LENGTH);
  result.set(ct, headerLength + SALT_LENGTH + IV_LENGTH);
  return result.buffer;
}

async function aesDecrypt(data: ArrayBuffer, passcodeSecret: ArrayBuffer, legacyPasscodeHash?: ArrayBuffer) {
  const dataArray = new Uint8Array(data);
  if (!hasMagic(dataArray)) {
    if (!legacyPasscodeHash) {
      throw new Error('[api/passcode] Unsupported legacy passcode blob');
    }

    return {
      plaintext: await aesDecryptLegacy(dataArray, legacyPasscodeHash),
      isLegacy: true,
    };
  }

  const version = dataArray[BLOB_MAGIC.length];
  if (version !== BLOB_VERSION) {
    throw new Error(`[api/passcode] Unsupported encrypted blob version: ${version}`);
  }

  const iterations = new DataView(dataArray.buffer, dataArray.byteOffset).getUint32(BLOB_MAGIC.length + 1, false);
  const saltLength = dataArray[BLOB_MAGIC.length + 5];
  const headerLength = BLOB_MAGIC.length + 1 + 4 + 1;
  const salt = dataArray.slice(headerLength, headerLength + saltLength);
  const ivStart = headerLength + saltLength;
  const iv = dataArray.slice(ivStart, ivStart + IV_LENGTH);
  const ct = dataArray.slice(ivStart + IV_LENGTH);
  const alg = { name: 'AES-GCM', iv };
  const key = await deriveAesKey(passcodeSecret, salt, ['decrypt'], iterations);
  const plainBuffer = await crypto.subtle.decrypt(alg, key, ct);

  return {
    plaintext: new TextDecoder().decode(plainBuffer),
    isLegacy: false,
  };
}

function hasMagic(data: Uint8Array) {
  if (data.length < BLOB_MAGIC.length) return false;
  return BLOB_MAGIC.every((byte, index) => data[index] === byte);
}

async function aesDecryptLegacy(dataArray: Uint8Array, pwHash: ArrayBuffer) {
  const iv = dataArray.slice(0, IV_LENGTH);
  const alg = { name: 'AES-GCM', iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['decrypt']);
  const ct = dataArray.slice(IV_LENGTH);
  const plainBuffer = await crypto.subtle.decrypt(alg, key, ct);

  return new TextDecoder().decode(plainBuffer);
}

async function deriveAesKey(
  passcodeSecret: ArrayBuffer,
  salt: Uint8Array,
  keyUsages: KeyUsage[],
  iterations = PBKDF2_ITERATIONS,
) {
  const keyMaterial = await crypto.subtle.importKey('raw', passcodeSecret, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    keyUsages,
  );
}
